import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import LoaderSpinner from "../../../../Components/pageLoader/LoaderSpinner";
import InventoryFilters from "./InventoryFilters";
import InventoryTable from "./InventoryTable";
import DropdownInventory from "../../../../Components/Modal/ModalInventory/DropdownInventory";

const API_URL = "https://server-gs.vercel.app/admin/products";
const CATEGORY_API = "https://server-gs.vercel.app/all-main-category";
const EVENT_API = "https://server-gs.vercel.app/eventCollection";

// Memory cache
const memoryCache = {};

const InventoryMain = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 16));

  const [categories, setCategories] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDropdown, setIsDropdown] = useState(false);

  const [filters, setFilters] = useState({
    dateFilter: "",
    fromDate: "",
    toDate: "",
    phoneNumber: "",
    category: "",
    selectedEventsData: "",
    SelectedType: "",
    productCode: "",
    title: "",
    promotionFilter: "",
    showWebFilter: "",
  });

  // Fetch categories & events once
  useEffect(() => {
    axios.get(CATEGORY_API).then(res => setCategories(res.data)).catch(console.error);
    axios.get(EVENT_API).then(res => setEventData(res.data)).catch(console.error);
  }, []);

  const getCacheKey = () => JSON.stringify({ ...Object.fromEntries(searchParams.entries()) });

  const fetchProducts = useCallback(async () => {
    const cacheKey = getCacheKey();

    if (memoryCache[cacheKey]) {
      const cached = memoryCache[cacheKey];
      setProducts(cached.products);
      setTotalPages(cached.totalPages);
      setCurrentPage(parseInt(searchParams.get("page") || "1", 10));
      return;
    }

    setIsLoading(true);
    try {
      const params = { ...Object.fromEntries(searchParams.entries()) };
      if (!params.page) params.page = currentPage;
      params.limit = params.limit || 16;

      const res = await axios.get(API_URL, { params });
      const data = res.data;

      setProducts(data.products || []);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(parseInt(params.page));

      memoryCache[cacheKey] = { products: data.products || [], totalPages: data.totalPages || 1 };
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, currentPage]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Filters
  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const handleApplyFilters = () => {
    const newParams = { page: 1 };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "") newParams[key] = value;
    });
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    setFilters({
      dateFilter: "",
      fromDate: "",
      toDate: "",
      phoneNumber: "",
      category: "",
      selectedEventsData: "",
      SelectedType: "",
      productCode: "",
      title: "",
      promotionFilter: "",
      showWebFilter: "",
    });
    setSearchParams({ page: 1 });
  };

  // Pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const params = Object.fromEntries(searchParams.entries());
      params.page = currentPage + 1;
      setSearchParams(params);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const params = Object.fromEntries(searchParams.entries());
      params.page = currentPage - 1;
      setSearchParams(params);
    }
  };

  // ✅ Delete: update UI & cache immediately
  const handleDeleteProduct = (productId) => {
    const cacheKey = getCacheKey();
    const cached = memoryCache[cacheKey] || { products, totalPages };
    const updatedProducts = cached.products.filter(p => p._id !== productId);
    memoryCache[cacheKey] = { products: updatedProducts, totalPages };
    setProducts(updatedProducts);
  };

  // ✅ Duplicate/Add: refetch page to show correct placement

const handleDuplicateProduct = () => {
  window.location.reload();
};

  const openModalPersonal = () => {
    setIsDropdown(true);
  };

  const closeModalPersonal = () => {
    setIsDropdown(false);
  };

    const toggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  if (isLoading) return <LoaderSpinner />;

  return (
    
 <div className="min-h-screen flex flex-col bg-gray-50">
      {/* ================= HEADER ================= */}
      <header className="w-full h-20 bg-white border-b border-gray-200 flex justify-between items-center px-6 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleFilter}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-blue-400 transition-all duration-200 shadow-sm"
          >
            {isFilterOpen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h18v2l-7 7v5l-4 4v-9L3 6V4z" />
              </svg>
            )}
            <span>{isFilterOpen ? "Close Filters" : "Filter Inventory"}</span>
          </button>

          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
            Inventory Management
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={openModalPersonal}
            className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 active:scale-95 focus:ring-4 focus:ring-blue-200 transition-all shadow-lg shadow-blue-100 flex items-center"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 3v14m7-7H3" />
            </svg>
            Add New Item
          </button>

          <DropdownInventory
            fetchProducts={fetchProducts}
            isVisible={isDropdown}
            onClose={closeModalPersonal}
          />
        </div>
      </header>

      {/* ================= MAIN CONTENT ================= */}
      <div className="flex flex-1 overflow-hidden">
        {/* ================= FILTER SIDEBAR ================= */}
        <aside
          className={`
            transition-all duration-300 ease-in-out
            ${isFilterOpen ? "w-[28%] opacity-100 translate-x-0" : "w-0 opacity-0 -translate-x-full"}
            overflow-hidden bg-white border-r border-gray-200 shadow-sm
          `}
        >
          <InventoryFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            categories={categories}
            eventData={eventData}
          />
        </aside>

        {/* ================= TABLE AREA ================= */}
        <main className="flex-1 overflow-auto transition-all duration-300">
          <InventoryTable
            products={products}
            fetchProducts={fetchProducts}
            handleDeleteProduct={handleDeleteProduct}
            handleDuplicateProduct={handleDuplicateProduct}
            currentPage={currentPage}
            totalPages={totalPages}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
          />
        </main>
      </div>
    </div>

  );
};

export default InventoryMain;
